import { useQuery, useMutation, useQueryClient } from 'react-query';
import { supabase } from '../lib/supabaseClient';
import { PopularQuery } from '../types/search';
import { useAuth } from '../components/auth/AuthContext';
import { useUserLikes } from './useUserLikes';

export const usePopularSearches = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isLiked, toggleLike: userToggleLike } = useUserLikes();

  const { data: queries = [], isLoading, error } = useQuery<PopularQuery[]>(
    ['popularSearches', user?.email],
    async () => {
      try {
        const { data: searches, error: searchError } = await supabase
          .from('searches')
          .select(`
            id,
            query,
            searches,
            likes,
            created_at,
            last_searched_at
          `)
          .order('searches', { ascending: false })
          .order('last_searched_at', { ascending: false })
          .limit(5);

        if (searchError) throw searchError;

        if (!searches) return [];

        return searches.map(search => ({
          id: search.id,
          query: search.query,
          searches: search.searches,
          likes: search.likes || 0,
          lastSearchedAt: search.last_searched_at,
          isLiked: isLiked(search.id)
        }));
      } catch (err) {
        console.error('Failed to fetch popular searches:', err);
        return [];
      }
    },
    {
      enabled: true,
      staleTime: 30000,
      cacheTime: 5 * 60 * 1000,
    }
  );

  const { mutateAsync: incrementSearchCount } = useMutation(
    async (query: string) => {
      const trimmedQuery = query.trim();
      if (!trimmedQuery) return null;

      try {
        // First try to update existing search
        const { data: existingSearch, error: selectError } = await supabase
          .from('searches')
          .select('id, searches')
          .eq('query', trimmedQuery)
          .maybeSingle();

        if (selectError) throw selectError;

        if (existingSearch) {
          // Update existing search
          const { data: updatedSearch, error: updateError } = await supabase
            .from('searches')
            .update({
              searches: (existingSearch.searches || 0) + 1,
              last_searched_at: new Date().toISOString()
            })
            .eq('id', existingSearch.id)
            .select()
            .single();

          if (updateError) throw updateError;
          return updatedSearch;
        } else {
          // Create new search
          const { data: newSearch, error: insertError } = await supabase
            .from('searches')
            .insert({
              query: trimmedQuery,
              searches: 1,
              last_searched_at: new Date().toISOString()
            })
            .select()
            .single();

          if (insertError) throw insertError;
          return newSearch;
        }
      } catch (err) {
        console.error('Failed to increment search count:', err);
        return null;
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['popularSearches', user?.email]);
      }
    }
  );

  // Use the toggleLike function from useUserLikes and update the UI optimistically
  const toggleLike = async (searchId: string) => {
    // Get the current state before the update
    const currentQueries = [...queries];
    const searchIndex = currentQueries.findIndex(q => q.id === searchId);
    
    if (searchIndex === -1) return;

    const wasLiked = currentQueries[searchIndex].isLiked;
    const likeDelta = wasLiked ? -1 : 1;

    // Optimistically update the UI
    queryClient.setQueryData(['popularSearches', user?.email], 
      currentQueries.map(query => 
        query.id === searchId 
          ? {
              ...query,
              isLiked: !wasLiked,
              likes: query.likes + likeDelta
            }
          : query
      )
    );

    try {
      // Perform the actual update
      await userToggleLike(searchId);
    } catch (error) {
      // Revert on error
      queryClient.setQueryData(['popularSearches', user?.email], currentQueries);
      console.error('Error toggling like:', error);
    }
  };

  return {
    queries,
    isLoading,
    error,
    incrementSearchCount,
    toggleLike
  };
};